<template>
  <div
    class="kanban-card-item"
    :class="{ 'kanban-card-item-confirming': isConfirming }"
  >
    <div class="kanban-card-item-tags">
      <img :src="card.avatar" class="kanban-card-item-avatar" alt="Usuário" />
      <div
        v-for="tag in card.listTag"
        :key="tag.name"
        class="kanban-card-item-tag"
        :style="{ background: tag.color }"
      >
        {{ tag.name }}
      </div>
    </div>

    <div class="kanban-card-item-name" @click="$emit('open', card)">
      <i class="fa-solid fa-building kanban-card-item-name-icon"></i>
      {{ card.customerName }}
    </div>

    <div v-if="card.description" class="kanban-card-item-description">
     <span v-html="card.description"></span>
    </div>

    <div class="kanban-card-item-footer">
      <div class="kanban-card-item-indicators">
        <div v-if="card.totalJob > 0" class="kanban-card-item-jobs">
          <div
            v-for="jobStatus in uniqueJobStatuses"
            :key="jobStatus.status"
            :class="jobStatusDotClass(jobStatus.status)"
            class="kanban-card-item-job-dot"
          ></div>
        </div>

        <div v-if="card.totalComment > 0" class="kanban-card-item-indicator">
          <i class="fa-regular fa-comments"></i>
          <span>{{ card.totalComment }}</span>
        </div>

        <div v-if="card.totalRent > 0" class="kanban-card-item-indicator">
          <i class="fas fa-exchange-alt"></i>
          <span>{{ card.totalRent }}</span>
          <span v-if="hasRule('Rental') && card.showValue" class="kanban-card-item-value">
            {{ card.totalValueRent | currency }}
          </span>
        </div>

        <div v-if="card.totalChecklist > 0" class="kanban-card-item-indicator">
          <i class="fa-regular fa-square-check"></i>
          <span>{{ card.totalChecklistDone }}/{{ card.totalChecklist }}</span>
        </div>
      </div>

      <div class="kanban-card-item-delete">
        <div v-if="!isConfirming" class="kanban-card-item-trash" @click="isConfirming = true">
          <i class="fa-solid fa-trash"></i>
        </div>
        <div v-else class="kanban-card-item-confirm-row">
          <div class="kanban-card-item-confirm-yes" @click="remove">
            <i class="fa-solid fa-circle-check"></i>
          </div>
          <div class="kanban-card-item-confirm-no" @click="isConfirming = false">
            <i class="fa-solid fa-xmark"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CardService from "../../services/card.service";

import { mapGetters, mapMutations } from "vuex";

const JOB_STATUS_DOTS = {
  "Hoje": "kanban-card-item-dot-today",
  "Pendente": "kanban-card-item-dot-late",
  "Conclu\u00EDda": "kanban-card-item-dot-done",
  "Prazo": "kanban-card-item-dot-scheduled",
};

export default {
  name: "KanbanCardItem",
  props: {
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isConfirming: false,
    };
  },
  computed: {
    ...mapGetters("user", ["hasRule"]),
    uniqueJobStatuses() {
      const seen = new Set();
      return (this.card.listStatusJob || []).filter((job) => {
        if (seen.has(job.status)) return false;
        seen.add(job.status);
        return true;
      });
    },
  },
  methods: {
    ...mapMutations("generic", ["addEvent", "removeLoading"]),
    jobStatusDotClass(status) {
      return JOB_STATUS_DOTS[status] || "";
    },
    async remove() {
      try {
        await CardService.remove(this.card.id);
        this.addEvent({ name: "cardRemoved" });
      } finally {
        this.isConfirming = false;
      }
    },
  },
};
</script>

<style scoped>
.kanban-card-item {
  background: var(--bg-base);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 10px;
  cursor: move;
  transition: box-shadow 0.15s, border-color 0.15s;
}

.kanban-card-item:hover {
  box-shadow: var(--shadow-sm);
  border-color: var(--border-subtle);
}

.kanban-card-item-confirming {
  border-color: var(--color-danger);
}

.kanban-card-item-tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
  min-height: 22px;
}

.kanban-card-item-avatar {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  object-fit: cover;
  flex-shrink: 0;
}

.kanban-card-item-tag {
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  border-radius: 999px;
  padding: 2px 8px;
  white-space: nowrap;
}

.kanban-card-item-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-brand-text);
  cursor: pointer;
  max-width: 270px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 6px;
  transition: opacity 0.15s;
}

.kanban-card-item-name:hover {
  text-decoration: underline;
  opacity: 0.85;
}

.kanban-card-item-name-icon {
  font-size: 12px;
  flex-shrink: 0;
}

.kanban-card-item-description {
  font-size: 12px;
  color: var(--text-secondary);
  background: var(--bg-elevated);
  border-radius: 999px;
  padding: 4px 10px;
  margin-bottom: 8px;
  max-width: 270px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kanban-card-item-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.kanban-card-item-indicators {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.kanban-card-item-jobs {
  display: flex;
  align-items: center;
  gap: 4px;
}

.kanban-card-item-job-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
}

::v-deep .kanban-card-item-dot-late { background-color: var(--color-danger); }
::v-deep .kanban-card-item-dot-done { background-color: var(--color-success); }
::v-deep .kanban-card-item-dot-today { background-color: var(--color-warning); }
::v-deep .kanban-card-item-dot-scheduled { background-color: var(--color-brand); }

.kanban-card-item-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-secondary);
}

.kanban-card-item-value {
  font-size: 12px;
  color: var(--text-primary);
  font-weight: 500;
}

.kanban-card-item-trash {
  font-size: 13px;
  color: var(--color-danger);
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.15s;
}

.kanban-card-item-trash:hover {
  opacity: 1;
}

.kanban-card-item-confirm-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.kanban-card-item-confirm-yes {
  font-size: 16px;
  color: var(--color-success);
  cursor: pointer;
}

.kanban-card-item-confirm-no {
  font-size: 16px;
  color: var(--color-danger);
  cursor: pointer;
}
</style>




